import React from "react"
import styled, { ThemeProvider } from "styled-components"

import useProductTracking from "hooks/useProductTracking"
import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import { when, maxWidth, minWidth } from "utils"

import Grid from "components/grid"
import GridAccordionItem from "components/grid-accordion-item"
import FeatureHighlight from "components/grid/feature"
import ProductHighlightCard from "components/card/product-highlight"
import PeopleScrollableGrid from "components/scrollable-grid/people"
import HomepageTabbed from "components/grid/homepage-tabbed"
import PodcastGrid from "components/podcast-grid"

import ContentfulVideo from "./video"
import ContentfulPersonCard from "./person"
import ContentfulCard from "./card"
import ContentfulClassModule from "./class-module"
import ContentfulTestimonialCard from "./testimonial"
import ContentfulTextBlurb from "./text"
import ContentfulButton from "./button"
import ContentfulTextWithMedia from "./text-with-media"
import Slider from "./slider"

const GRID_COLUMN_SIZES_PER_TYPE = {
  ContentfulPerson: {
    default: 6,
    desktopL: 5,
    desktop: 4,
    tablet: 3,
    mobile: 2,
  },
  ContentfulCard: {
    default: 4,
    desktopL: 3,
    desktop: 3,
    tablet: 2,
    mobile: 1,
  },
  ContentfulProductHighlight: {
    default: 4,
    desktopL: 3,
    desktop: 3,
    tablet: 2,
    mobile: 1,
  },
  ContentfulAccordionItem: {
    default: 1,
    desktopL: 1,
    desktop: 1,
    tablet: 1,
    mobile: 1,
  },
  ContentfulFeatureHighlight: {
    tablet: 3,
    mobile: 2,
  },
  ContentfulClassModule: {
    default: 2,
    desktopL: 2,
    desktop: 2,
    tablet: 2,
    mobile: 1,
  },
  ContentfulTestimonial: {
    default: 3,
    desktopL: 2,
    desktop: 2,
    tablet: 2,
    mobile: 1,
  },
  ContentfulTextWithMedia: {
    default: 3,
    desktopL: 3,
    desktop: 3,
    tablet: 2,
    mobile: 1,
  },
  ContentfulTextBlurb: {
    default: 2,
    desktopL: 2,
    desktop: 2,
    tablet: 2,
    mobile: 1,
  },
  ContentfulGridLayout: {
    default: 1,
    desktopL: 1,
    desktop: 1,
    tablet: 2,
    mobile: 1,
  },
}

const ROW_MARGINS_PER_TYPE = {
  ContentfulPerson: "1.5rem",
  ContentfulCard: "1.5rem",
  ContentfulProductHighlight: "1.5rem",
  ContentfulAccordionItem: "1.6rem",
  ContentfulFeatureHighlight: "4.5rem",
  ContentfulClassModule: "3rem",
  ContentfulTestimonial: "2rem",
  ContentfulTextWithMedia: "2rem",
  ContentfulTextBlurb: "0rem",
}

const COL_MARGINS_PER_TYPE = {
  ContentfulPerson: "2.6rem",
  ContentfulCard: "1.5rem",
  ContentfulProductHighlight: "1.5rem",
  ContentfulAccordionItem: "0rem",
  ContentfulFeatureHighlight: "2.4rem",
  ContentfulClassModule: "3rem",
  ContentfulTestimonial: "2rem",
  ContentfulTextWithMedia: "2rem",
  ContentfulTextBlurb: "0rem",
}

const ContentfulGrid = ({
  component,
  onTop,
  elRef,
  isNested,
  isFocusLayout,
  leftFocus,
  isFocusedItem,
  isRailItem,
  parentLayout,
  layoutStyle,
  isSliderLayout,
  isExpandedSlider,
  isCollapsedSlider,
  sliderIndex,
  currentDate,
  isMobileStacked,
  isRightRail,
  ...props
}) => {
  const {
    id,
    mobileHorizontalScroll,
    preHeadingText,
    heading,
    subheading,
    subheadingLogo,
    bodyText,
    backgroundColor,
    mobileItemCount,
    largeDesktopColumns,
    desktopColumns,
    tabletColumns,
    mobileColumns,
    items,
    scrollable,
    navigationAnchor,
    internalID,
    headingFont,
    button,
    buttonPosition,
    internal,
    hideOnPageLoad,
    theme,
  } = component

  if (!items || !items.length) {
    return null
  }

  const itemType = items[0].internal.type
  if (!GRID_COLUMN_SIZES_PER_TYPE[itemType]) return null

  const { trackProductListView } = useProductTracking()
  const { classesOnPage, isHomePage } = useComponentizedPage()

  if (itemType === "ContentfulCard" && items[0].itemType === "Class") {
    trackProductListView({
      elementRef: elRef,
      trackingId: internalID,
      items: items.map((item) => ({
        ...item,
        cls: classesOnPage.find((c) => `${c.id}` === item.itemId),
      })),
    })
  }

  if (itemType === "ContentfulCard" && items[0].itemType === "Product") {
    trackProductListView({ elementRef: elRef, trackingId: internalID, items })
  }

  const numColumns = {
    default: largeDesktopColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].default,
    desktopL:
      largeDesktopColumns === 4
        ? 2
        : largeDesktopColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].desktopL,
    desktop:
      largeDesktopColumns === 4
        ? 2
        : desktopColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].desktop,
    tablet:
      largeDesktopColumns === 4 ? 2 : tabletColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].tablet,
    mobile: mobileColumns || GRID_COLUMN_SIZES_PER_TYPE[itemType].mobile,
  }

  // Force single column layout for rail items in focus layouts
  if (isRailItem && isFocusLayout) {
    numColumns.default = 1
    numColumns.desktopL = 1
    numColumns.desktop = 1
    numColumns.tablet = 1
    // Keep mobile as is, or force to 1 as well
    numColumns.mobile = 1
  }

  // For rail items on mobile, adjust to show 1.5 cards
  if (isRailItem) {
    // Handle with CSS by making cards wider and enabling horizontal scroll
    numColumns.mobile = 1.6 // This will be handled in the Grid component
  }

  if (itemType === "ContentfulFeatureHighlight") {
    const rowSize = Math.min(6, items.length)
    numColumns.default = largeDesktopColumns || rowSize
    numColumns.desktopL = largeDesktopColumns || rowSize
    numColumns.desktop = desktopColumns || rowSize
  }

  let rowMargin = ROW_MARGINS_PER_TYPE[itemType]
  let colMargin = COL_MARGINS_PER_TYPE[itemType]

  // Increase column margin for rail items to create proper gaps
  if (isRailItem) {
    colMargin = "3rem"
  }
  if (itemType === "ContentfulTestimonial") {
    if ((items[0].attribution && items[0].attribution.image) || items[0].attributionImage) {
      colMargin = "5.4rem"
    }
  }
  if (itemType === "ContentfulCard" && items[0].itemType === "Podcast") {
    rowMargin = "3rem"
    colMargin = "3rem"
  }
  if (itemType === "ContentfulCard" && items[0].itemType === "Navigation") {
    rowMargin = "0"
    colMargin = "1.4rem"
  }

  const title = heading?.childMarkdownRemark?.html?.replace(/^<p>(.*)<\/p>$/i, "$1")

  const hideOnMobile = (i) => mobileItemCount && mobileItemCount > 0 && i >= mobileItemCount
  const useSlimTitle = isHomePage && ["Article", "Podcast", "Link"].includes(items[0].itemType)

  const showDescriptionOnMobile =
    isHomePage &&
    (["Article", "Podcast", "Link"].includes(items[0].itemType) ||
      items[0].internal.type === "ContentfulPerson")

  const useHorizontalScrollOnMobile =
    isHomePage && ["Article", "Podcast", "Person"].includes(items[0].itemType)

  const enforceCardWidth =
    isHomePage && ["Article", "Podcast", "Person"].includes(items[0].itemType)
  const enforceCardColumnGap = useHorizontalScrollOnMobile && !isNested
  const enforceCardRowGap = isHomePage && items[0].internal.type === "ContentfulPerson"
  const enforceMarginDecrease = items[0].internal.type === "ContentfulGridLayout"

  if (isSliderLayout && layoutStyle === "Slider") {
    return (
      <Slider
        isExpandedSlider={isExpandedSlider}
        sliderIndex={sliderIndex}
        items={items}
        preHeadingText={preHeadingText}
        subheadingLogo={subheadingLogo}
        title={title}
        bodyText={bodyText}
        subheading={subheading}
        button={button}
        backgroundColor={backgroundColor}
        onTop={onTop}
        theme={theme}
        isMobileStacked={isMobileStacked}
      />
    )
  }
  // Special handling for people scrollable grid on home page (collective and faculty sections)
  const isPeopleLayout =
    isHomePage &&
    (internalID.toLowerCase().includes("collective") ||
      internalID.toLowerCase().includes("faculty")) &&
    items[0].internal.type === "ContentfulPerson"

  // If it's a collective layout, use the CollectiveGrid component
  if (isPeopleLayout) {
    // Transform ContentfulPerson items to the format expected by CollectiveGrid
    const transformedItems = items.map((item) => ({
      id: item.id,
      name: item.name,
      image: item.image,
      classes: item.classes,
    }))

    const showClassTags =
      internalID.toLowerCase().includes("faculty") && items[0].internal.type === "ContentfulPerson"

    return (
      <PeopleScrollableGrid
        title={title}
        subtitle={subheading && subheading.childMarkdownRemark.html}
        items={transformedItems}
        showClassTags={showClassTags}
      />
    )
  }

  if (props.isTabsLayout) {
    return (
      <HomepageTabbed
        key={items.id}
        items={items || []}
        button={button}
        buttonPosition={buttonPosition}
      />
    )
  }

  // Special handling for podcast rail items
  if (isRailItem && itemType === "ContentfulCard" && items[0].itemType === "Podcast") {
    return (
      <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
        <PodcastGrid
          title={title}
          items={items}
          button={button}
          backgroundColor={backgroundColor}
          onTop={onTop}
          theme={theme}
        />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme || null)}>
      <Grid
        hideOnPageLoad={hideOnPageLoad}
        dataId={id}
        id={navigationAnchor && navigationAnchor.anchorId}
        elRef={elRef}
        mobileHorizontal={mobileHorizontalScroll || isRailItem} // Enable horizontal for rails
        preHeading={preHeadingText && preHeadingText.childMarkdownRemark.html}
        title={title}
        subtitle={subheading && subheading.childMarkdownRemark.html}
        logo={subheadingLogo?.length && subheadingLogo[0].secure_url}
        logoAspectRatio={
          subheadingLogo?.length && subheadingLogo[0].height / subheadingLogo[0].width
        }
        body={bodyText && bodyText.childMarkdownRemark.html}
        mobileItemCount={mobileItemCount}
        numColumns={numColumns}
        rowMargin={rowMargin}
        colMargin={colMargin}
        unevenColumns={itemType === "ContentfulClassModule"}
        useMonoline={headingFont === "mbg Monoline"}
        buttonElement={button && <ContentfulButton component={button} />}
        buttonPosition={buttonPosition}
        textComponent={itemType === "ContentfulTextBlurb"}
        mobileNoBorder
        useSlimTitle={useSlimTitle}
        showDescriptionOnMobile={showDescriptionOnMobile}
        useHorizontalScrollOnMobile={useHorizontalScrollOnMobile}
        isScrollable={scrollable}
        items={scrollable ? items : null}
        isNested={isNested}
        enforceMarginDecrease={enforceMarginDecrease}
        hideText={items[0].itemType === "Navigation"}
        isNavigation={items[0].itemType === "Navigation"}
        isFocusLayout={isFocusLayout}
        leftFocus={leftFocus}
        isFocusedItem={isFocusedItem}
        isRailItem={isRailItem}
        parentLayout={parentLayout}
        currentDate={currentDate}
        {...props}
      >
        {items.map((item, i) => {
          let card

          switch (item.internal.type) {
            case "ContentfulGridLayout":
              card = (
                <ContentfulGrid
                  key={`nested-grid-${item.id}`}
                  component={item}
                  isNested
                  isFocusLayout={isFocusLayout}
                  leftFocus={leftFocus}
                  isFocusedItem={isFocusedItem}
                  isRailItem={isRailItem}
                  parentLayout={parentLayout}
                  layoutStyle={layoutStyle}
                  isSliderLayout={isSliderLayout}
                  isExpandedSlider={isExpandedSlider}
                  isCollapsedSlider={isCollapsedSlider}
                  sliderIndex={sliderIndex}
                />
              )
              break
            case "ContentfulVideo":
              card = <ContentfulVideo component={item} isGrid fullHeight nested />
              break

            case "ContentfulPerson":
              card = (
                <ContentfulPersonCard
                  key={item.id}
                  component={item}
                  isNested={isNested}
                  isHomePage={isHomePage}
                />
              )
              break

            case "ContentfulCard":
              // Use PodcastRailCard for podcast items in rail layouts
              if (isRailItem && item.itemType === "Podcast") {
                card = <div key={item.id} />
              } else {
                card = (
                  <ContentfulCard
                    key={item.id}
                    component={item}
                    variant={
                      numColumns.default === 2 ? "2col" : numColumns.default === 3 ? "3col" : "4col"
                    }
                    headlineVariant={
                      numColumns.default === 2
                        ? "xlarge"
                        : numColumns.default === 3
                        ? "large"
                        : "medium"
                    }
                    isGrid={internal.type === "ContentfulGridLayout"}
                    fullHeight
                    {...(isHomePage && {
                      pageType: "Landing",
                      zoomImageOnHover: items[0].itemType !== "Navigation",
                    })}
                    {...(useHorizontalScrollOnMobile && { explicitWidth: "316px" })}
                    isScrollable={scrollable}
                    isHomePage={isHomePage}
                    renderImage={
                      !(isRailItem && (item.itemType === "Article" || item.itemType === "Podcast"))
                    }
                    lastItem={i === items.length - 1}
                    {...(props?.closeButtonRef &&
                      props?.closeButtonRef.current && { closeButtonRef: props.closeButtonRef })}
                    isRailItem={isRailItem}
                    mobileHeadlineSize={isRailItem ? "1.2rem" : undefined}
                  />
                )
              }
              break

            case "ContentfulFeatureHighlight":
              card = (
                <FeatureHighlight
                  key={item.id}
                  cloudinaryUrl={item.image ? item.image[0].secure_url : null}
                  value={item.value}
                  label={item.label}
                />
              )
              break

            case "ContentfulTextWithMedia":
              card = <ContentfulTextWithMedia key={item.id} component={item} inGrid />
              break

            case "ContentfulProductHighlight":
              card = (
                <ProductHighlightCard
                  key={item.id}
                  preheading={item.preHeadingText && item.preHeadingText.childMarkdownRemark.html}
                  headline={
                    item.heading &&
                    item.heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
                  }
                  subheadline={item.description && item.description.childMarkdownRemark.html}
                  byline={item.subLabel}
                  image={item.image && item.image[0].secure_url}
                  isGrid={internal.type === "ContentfulGridLayout"}
                />
              )
              break

            case "ContentfulAccordionItem": {
              const itemTitle = item.headline.childMarkdownRemark.html.replace(
                /^<p>(.*)<\/p>$/i,
                "$1",
              )
              card = (
                <GridAccordionItem
                  key={item.id}
                  title={itemTitle}
                  content={item.bodyText.childMarkdownRemark.html}
                  singleCol={numColumns.default === 1}
                  preheading={item.preheading}
                />
              )
              break
            }

            case "ContentfulClassModule":
              card = <ContentfulClassModule key={item.id} component={item} />
              break

            case "ContentfulTestimonial":
              card = <ContentfulTestimonialCard key={item.id} component={item} nested />
              break

            case "ContentfulTextBlurb":
              card = <ContentfulTextBlurb key={item.id} component={item} inGrid />
              break

            case "HomepageTabbed":
              card = <HomepageTabbed key={item.id} items={item.items || []} />
              break

            default:
              card = <div />
              break
          }

          return (
            <MobileContainer
              key={item.id}
              hideOnMobile={hideOnMobile(i)}
              isRightRail={isRightRail}
              isScrollable={scrollable}
              isRailItem={isRailItem}
              {...(enforceCardWidth && { width: "31.6rem" })}
              {...(enforceCardColumnGap && { columnGap: "1.8rem" })}
              {...(enforceCardRowGap && { rowGap: "1.8rem" })}
            >
              {card}
            </MobileContainer>
          )
        })}
      </Grid>
    </ThemeProvider>
  )
}

const MobileContainer = styled.div`
  ${when("hideOnMobile")`
    display: none;
  `}

  ${minWidth.mobile`
    display: block;
  `}
  
  ${maxWidth.mobile`
    ${when("width")`
      width: ${({ width }) => width};
    `}

    ${when("columnGap")`
      margin: 0 ${({ columnGap }) => columnGap};

      &:first-of-type {
        margin-left: 0;
      }

      &:last-of-type {
        margin-right: 0;
      }
    `}

    ${when("rowGap")`
      margin-top: ${({ rowGap }) => rowGap};
    `}

    /* Rail items on mobile: enable horizontal scroll to show 1.5 cards */
    ${({ isRailItem }) =>
      isRailItem &&
      `
      flex: 0 0 55%; /* Each card takes 55% width, showing ~1.5 cards with more gaps */
      margin-right: 3rem !important; /* Even larger gap between cards - override other margins */
      
      &:last-of-type {
        margin-right: 3rem !important; /* Add more padding at the end */
      }
    `}

    ${({ isRightRail, isScrollable, isRailItem }) =>
      !isRightRail &&
      !isScrollable &&
      !isRailItem &&
      `
      margin: 0;
    `}
  `}
`

export default ContentfulGrid
